import {
    DropTarget,
    NOT_ENOUGH_SEATS_MESSAGE,
    SEAT_OCCUPIED_MESSAGE,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { EmptySeatDropType } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/dropTypes';
import {
    DropReducer,
    EmptySeatDropResult,
    ReducerState,
    StoreAction,
} from 'assets/js/pages/campaign-settings/campaign-event/table-seating/reducers/types';
import { DropTargetPayload } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/types';

const emptySeatReducer: DropReducer = (state, action) => {
    const { activeData, overData, selectedTicketIds = [] } = action.payload;

    if (!isValidEmptySeatTarget(overData)) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'Invalid drop target' },
        };
    }

    const ticketIds = extractTicketIds(activeData, selectedTicketIds);
    if (ticketIds.length === 0) {
        return {
            action: StoreAction.SHOW_ERROR,
            payload: { message: 'No tickets selected' },
        };
    }

    const scenario = determineEmptySeatScenario(state, ticketIds, overData);

    switch (scenario.type) {
        case EmptySeatDropType.SINGLE_TO_EMPTY:
            return {
                action: StoreAction.SEAT_GUEST,
                payload: {
                    ticketId: scenario.ticketId,
                    tableId: scenario.tableId,
                    seatNumber: scenario.seatNumber,
                },
            };

        case EmptySeatDropType.SINGLE_TO_OCCUPIED:
            return {
                action: StoreAction.SHOW_ERROR,
                payload: {
                    message: SEAT_OCCUPIED_MESSAGE,
                },
            };

        case EmptySeatDropType.MULTIPLE_WITH_CAPACITY:
            return {
                action: StoreAction.RESHUFFLE_AND_SEAT,
                payload: {
                    ticketIds: scenario.ticketIds,
                    tableId: scenario.tableId,
                    seatNumber: scenario.seatNumber,
                },
            };

        case EmptySeatDropType.MULTIPLE_EXCEEDS_CAPACITY:
            return {
                action: StoreAction.SHOW_ERROR,
                payload: {
                    message: NOT_ENOUGH_SEATS_MESSAGE,
                },
            };

        case EmptySeatDropType.INVALID:
        default:
            return {
                action: StoreAction.SHOW_ERROR,
                payload: { message: 'Invalid drop action' },
            };
    }
};

const determineEmptySeatScenario = (
    state: ReducerState,
    ticketIds: string[],
    overData: DropTargetPayload,
): EmptySeatDropResult => {
    const { tableId, seatNumber } = overData;

    if (!tableId || seatNumber === undefined) {
        return { type: EmptySeatDropType.INVALID };
    }

    if (ticketIds.length === 1) {
        const seatOccupied = isSeatOccupied(state, tableId, seatNumber, ticketIds[0]);

        return {
            type: seatOccupied
                ? EmptySeatDropType.SINGLE_TO_OCCUPIED
                : EmptySeatDropType.SINGLE_TO_EMPTY,
            ticketId: ticketIds[0],
            tableId,
            seatNumber,
        };
    }

    const hasCapacity = checkTableCapacity(state, tableId, ticketIds);

    return {
        type: hasCapacity
            ? EmptySeatDropType.MULTIPLE_WITH_CAPACITY
            : EmptySeatDropType.MULTIPLE_EXCEEDS_CAPACITY,
        ticketIds,
        tableId,
        seatNumber,
    };
};

const isValidEmptySeatTarget = (overData: DropTargetPayload): boolean => {
    return (
        overData.type === DropTarget.EMPTY_SEAT &&
        !!overData.tableId &&
        overData.seatNumber !== undefined
    );
};

const extractTicketIds = (activeData: DropTargetPayload, selectedTicketIds: string[]): string[] => {
    if (activeData.type === DropTarget.TICKET && activeData.ticketId) {
        return [activeData.ticketId, ...selectedTicketIds.filter(id => id !== activeData.ticketId)];
    }
    return selectedTicketIds;
};

const isSeatOccupied = (
    state: ReducerState,
    tableId: string,
    seatNumber: number,
    excludeTicketId: string,
): boolean => {
    return Object.values(state.tickets).some(
        t =>
            t.seating_group_id === tableId &&
            t.seat_number === seatNumber &&
            t.id !== excludeTicketId,
    );
};

const checkTableCapacity = (
    state: ReducerState,
    tableId: string,
    incomingTicketIds: string[],
): boolean => {
    const table = state.seatingGroups[tableId];
    if (!table?.capacity) {
        return true;
    }

    const currentOccupantCount = Object.values(state.tickets).filter(
        t => t.seating_group_id === tableId && !incomingTicketIds.includes(t.id),
    ).length;

    return currentOccupantCount + incomingTicketIds.length <= table.capacity;
};

export { emptySeatReducer };
